import React from 'react';
import { RecordContactSnapshot } from '../../types/contactSnapshot';

type Props = {
  snapshots?: RecordContactSnapshot[] | null;
  title?: string;
  subtitle?: string;
  emptyText?: string;
};

const ROLE_LABELS: Record<string, string> = {
  matched_customer: 'Matched SCSW Customer',
  billing_contact: 'Billing Contact',
  invoice_recipient: 'Invoice Recipient',
  trip_contact: 'Trip Contact',
  passenger: 'Passenger',
  account_primary_billing: 'Account Primary Billing',
};

function formatRole(role?: string | null) {
  if (!role) return 'Contact';
  return ROLE_LABELS[role] || role.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatSource(source?: string | null) {
  if (!source) return null;
  const normalized = source.toLowerCase();
  if (normalized === 'backfill_from_legacy_fields') {
    return 'Historical Record';
  }
  if (normalized === 'invoice_import') {
    return 'Invoice Import';
  }
  if (normalized === 'customer_portal') {
    return 'Customer Portal';
  }
  return source.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function contactLabel(snapshot: RecordContactSnapshot) {
  return snapshot.name || snapshot.companyName || snapshot.email || 'Unnamed contact';
}

export default function RecordContactSnapshotsPanel({
  snapshots,
  title = 'Record Contacts',
  subtitle = 'Contacts saved with this record',
  emptyText = 'No contact snapshots have been written for this record yet.',
}: Props) {
  const visibleSnapshots = Array.isArray(snapshots)
    ? snapshots.filter((snapshot) => snapshot && (snapshot.name || snapshot.email || snapshot.phone || snapshot.companyName))
    : [];

  return (
    <div className="card p-4">
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-on-surface">{title}</h4>
        <p className="mt-1 text-xs text-on-surface-variant">{subtitle}</p>
      </div>

      {visibleSnapshots.length ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {visibleSnapshots.map((snapshot, index) => {
            const source = formatSource(snapshot.source);
            return (
              <div
                key={`${snapshot.id || snapshot.role}-${snapshot.email || snapshot.name || index}`}
                className="rounded-card border border-outline/50 bg-surface-container-low p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                    {formatRole(snapshot.role)}
                  </p>
                  {source ? (
                    <span className="rounded-pill bg-surface-container-high px-2 py-0.5 text-[11px] font-medium text-on-surface-variant">
                      {source}
                    </span>
                  ) : null}
                </div>
                <p className="mt-2 truncate text-sm font-semibold text-on-surface">
                  {contactLabel(snapshot)}
                </p>
                {snapshot.email ? (
                  <p className="break-all text-xs text-on-surface-variant">{snapshot.email}</p>
                ) : null}
                {snapshot.phone ? (
                  <p className="text-xs text-on-surface-variant">{snapshot.phone}</p>
                ) : null}
                {snapshot.companyName && snapshot.companyName !== snapshot.name ? (
                  <p className="text-xs text-on-surface-variant">{snapshot.companyName}</p>
                ) : null}
                {snapshot.externalCustomerId ? (
                  <p className="mt-2 font-mono text-[11px] text-on-surface-variant">
                    SCSW {snapshot.externalCustomerId}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="rounded-card border border-dashed border-ghost bg-surface-container-low p-3 text-sm text-on-surface-variant">
          {emptyText}
        </p>
      )}
    </div>
  );
}
